import { GalleryClickActionType, GalleryWidgetData, IGalleryImageData, IGalleryWidgetData, IGalleryWidgetDataShort, IGalleryWidgetStyle, InfoPosition } from "@pageBuilder/typings";
import { IRenderingCommonContext } from "@commonRendering/types";
import { CssProperties } from "../../../models/css-property";
import { Base64Util } from "../../../utils/base64-util";
import { StringUtil } from "../../../utils/string-util";
import { LinkRenderer } from "../link.renderer";
import { VerticalAlignmentRenderer } from "../vertical-alignment-renderer";
import { WidgetRendererBase } from "../widget-renderer-base";
import { GalleryScriptRenderer } from "./gallery-script.renderer";

export class GalleryWidgetRenderer extends WidgetRendererBase<IGalleryWidgetData> {

    private readonly DATA_ATTRIBUTE = "data-gallery";
    private readonly scriptRenderer: GalleryScriptRenderer;
    private readonly linkRenderer: LinkRenderer;
    private readonly verticalAlignmentRenderer: VerticalAlignmentRenderer;

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);
        this.scriptRenderer = new GalleryScriptRenderer();
        this.linkRenderer = new LinkRenderer();
        this.verticalAlignmentRenderer = new VerticalAlignmentRenderer();
    }

    protected createNewInternal(): HTMLElement {
        return this.createWidgetElement("galleryWidget");
    }

    protected renderInternal(element: HTMLElement, data: IGalleryWidgetData): HTMLElement {
        this.ensureImagesFileName(data);

        if (data.images?.length >= 1) {
            this.removePlaceholder(element);
            this.ensureEditorAttributes(element, data);
            this.renderImages(element, data);
        } else {
            this.renderPlaceholder(element);
            this.removeGalleryWrapper(element);
        }

        this.renderStyles(element, data);
        this.setAttributes(element, data);
        this.renderScript(element, data);

        return element;
    }

    private ensureEditorAttributes(element: HTMLElement, data: IGalleryWidgetData): void {
        if (data.hoverAnimationType === "no-change" || this.isPreviewRendering) {
            return;
        }

        element.dataset.editorMode = "";
    }

    //Required for galleries created before the fileName property appears
    private ensureImagesFileName(data: IGalleryWidgetData): void {
        let index = 1;
        data.images.forEach(image => {
            if (StringUtil.isNullOrWhiteSpace(image.fileName)) {
                image.fileName = `My Image ${index}`;
                index++;
            }
        });
    }

    private removePlaceholder(element: HTMLElement): void {
        const notAvailableElement = element.querySelector<HTMLDivElement>(".not-available");
        notAvailableElement?.remove();
    }

    private renderPlaceholder(element: HTMLElement): void {
        let notAvailableElement = element.querySelector<HTMLDivElement>(".not-available");
        if (!notAvailableElement) {
            notAvailableElement = document.createElement("div");
            notAvailableElement.classList.add("not-available");
            const content = element.querySelector<HTMLElement>(".widget-content");
            content?.appendChild(notAvailableElement);
        }
    }

    private renderImages(element: HTMLElement, data: IGalleryWidgetData): void {
        if (!data.images) {
            return;
        }

        const galleryItems: HTMLElement[] = [];

        const galleryWrapper = this.ensureImagesWrapper(element, data);
        if (galleryWrapper.hasChildNodes()) {
            galleryWrapper.innerHTML = "";
        }

        for (const image of data.images) {
            const galleryItem = this.ensureGalleryItem(element, image, data.actionType);
            const imageWrapper = this.createImageElement(galleryItem, data, image);
            const contentWrapper = this.createContentWrapperElement(galleryItem, data, image);

            galleryItem.classList.toggle("on-image", data.infoPosition === InfoPosition.OnImage);
            if (galleryItem.hasChildNodes()) {
                galleryItem.innerHTML = "";
            }

            switch (data.infoPosition) {
                case InfoPosition.Above: {
                    galleryItem.append(contentWrapper);
                    galleryItem.append(imageWrapper);
                    break;
                }
                case InfoPosition.OnImage: {
                    imageWrapper.append(contentWrapper);
                    galleryItem.append(imageWrapper);
                    break;
                }
                default: {
                    galleryItem.append(imageWrapper);
                    galleryItem.append(contentWrapper);
                    break;
                }
            }
            galleryItems.push(galleryItem);
        }

        let index = 0;
        for (let i = 0; i < data.images.length; i++) {
            if (index > data.style.imageInRow - 1) {
                index = 0;
            }

            const galleryItem = galleryItems[i];
            galleryItem.setAttribute("data-index", i.toString());
            galleryItem.style.order = i.toString();

            galleryWrapper.appendChild(galleryItem);
        }

        const content = element.querySelector(".widget-content");
        content?.append(galleryWrapper);
    }

    private ensureImagesWrapper(element: HTMLElement, data: IGalleryWidgetData): HTMLElement {
        let wrapper = element.querySelector<HTMLElement>(".gallery-wrapper");

        if (!wrapper) {
            wrapper = document.createElement("div");
            wrapper?.classList.add("gallery-wrapper");
        }

        wrapper.classList.toggle("one-image-in-row", data.style.imageInRow === 1);

        return wrapper;
    }

    private ensureGalleryItem(element: HTMLElement, image: IGalleryImageData, clickActionType: GalleryClickActionType): HTMLElement {
        let galleryItem = element.querySelector<HTMLElement>(".gallery-item");
        const link = document.createElement("a");
        link.classList.add("gallery-item");
        link.setAttribute("tabindex", "-1");
        const imageEl = galleryItem?.querySelector(".image");

        if (!galleryItem || imageEl?.id !== image.id) {
            galleryItem = document.createElement("div");
            galleryItem.classList.add("gallery-item");
        }

        link.innerHTML = galleryItem.innerHTML;

        if (clickActionType === GalleryClickActionType.OpenLink && image.linkData?.type !== "none") {
            return this.linkRenderer.render(link, image.linkData);
        }

        galleryItem.classList.toggle("none-link",
            clickActionType === GalleryClickActionType.None ||
            (clickActionType === GalleryClickActionType.OpenLink && image.linkData?.type === "none")
        );
        return galleryItem;
    }

    private createImageElement(element: HTMLElement, data: IGalleryWidgetData, image: IGalleryImageData): HTMLElement {
        let imageWrapper = element.querySelector<HTMLElement>(".image-wrapper");

        if (!imageWrapper) {
            imageWrapper = document.createElement("div");
            imageWrapper.classList.add("image-wrapper");
        }

        let img = element.querySelector<HTMLImageElement>("img");
        if (!img) {
            img = document.createElement("img");
            img.classList.add("image");

            img.setAttribute("draggable", "false");

            if (img.getAttribute("src") !== image.src) {
                img.src = image.src || "";
            }

            img.id = image.id ?? "";
        }

        img.alt = image.altText || "";

        if (data.imgRatio) {
            imageWrapper.classList.remove("crop-content-wrapper", "ratio-16-9", "ratio-4-3", "ratio-normal", "ratio-3-4", "ratio-9-16");

            if (data.imgRatio !== "original") {
                img?.classList.add("crop-content");
                imageWrapper.classList.add("crop-content-wrapper", data.imgRatio.toString());
            } else {
                img?.classList.remove("crop-content");
            }
        }

        imageWrapper.append(img);
        return imageWrapper;
    }

    private createContentWrapperElement(element: HTMLElement, data: IGalleryWidgetData, image: IGalleryImageData): HTMLElement {
        let contentWrapper = element.querySelector<HTMLElement>(".content-wrapper");

        if (!contentWrapper) {
            contentWrapper = document.createElement("div");
            contentWrapper.classList.add("content-wrapper");
        }

        this.ensureTitle(contentWrapper, data.isNeedTitle, image.title ?? "");
        this.ensureDescription(contentWrapper, data.isNeedDescription, image.description ?? "");

        contentWrapper.classList.remove(InfoPosition.Above.toString(), InfoPosition.Underneath.toString(), "no-change", "appears", "disappears");
        const isEmptyContent = (!data.isNeedTitle && !data.isNeedDescription) || (!image.title && !image.description) || (data.infoPosition === InfoPosition.OnImage);
        if (!isEmptyContent) {
            contentWrapper.classList.add(data.infoPosition.toString());
        }

        contentWrapper.classList.add(data.hoverAnimationType?.toString() ?? "no-change");

        return contentWrapper;
    }

    private ensureTitle(contentWrapper: HTMLElement, isNeedTitle: boolean, titleContent: string): void {
        let title = contentWrapper.querySelector<HTMLParagraphElement>(".title");

        if (!title) {
            title = document.createElement("p");
            title.classList.add("title");
        }

        title.classList.toggle("hidden", !isNeedTitle);
        title.textContent = titleContent;

        contentWrapper.append(title);
    }

    private ensureDescription(contentWrapper: HTMLElement, isNeedDescription: boolean, descriptionContent: string): void {
        let description = contentWrapper.querySelector<HTMLParagraphElement>(".description");

        if (!description) {
            description = document.createElement("p");
            description.classList.add("description");
        }

        description.classList.toggle("hidden", !isNeedDescription);
        description.textContent = descriptionContent;

        contentWrapper.append(description);
    }

    protected renderStyles(element: HTMLElement, data: IGalleryWidgetData): void {
        if (!data.style) {
            this.cleanupStyle(element);
            return;
        }

        this.renderBaseStyle(element, data.style);
        this.renderPaginationStyle(element, data.style);
        this.renderTextStyle(element, data.style);
        this.renderImagesWrapperStyle(element, data.style);
    }

    private renderBaseStyle(element: HTMLElement, style: IGalleryWidgetStyle): void {
        this.animationRenderer.setAnimationStyles(element, style.animation);
    }

    private renderPaginationStyle(element: HTMLElement, style: IGalleryWidgetStyle): void {
        if (style.pagination.isLoadMoreButtonVisible) {
            element.setAttribute("load-more-button", "");
        } else {
            element.removeAttribute("load-more-button");
        }

        const buttonWrapperProp = new CssProperties();
        buttonWrapperProp.tryAdd("--button-alignment", style.pagination.alignment);
        this.styleService.setStyle(element, `#${element.id} .button-wrapper`, buttonWrapperProp.data);

        const buttonProp = new CssProperties();
        buttonProp.tryAdd("color", style.pagination.loadMoreButtonColor ?? GalleryWidgetData.defaultStyle.pagination.loadMoreButtonColor);
        this.styleService.setStyle(element, `#${element.id} .load-more-button`, buttonProp.data);
    }

    private renderImagesWrapperStyle(element: HTMLElement, style: IGalleryWidgetStyle): void {
        const imagesProp = new CssProperties();

        imagesProp.tryAdd("--space-between-images", `${style.spaceBetweenImages.toString() ?? GalleryWidgetData.defaultStyle.spaceBetweenImages}px`);
        imagesProp.tryAdd("--image-in-row", style.imageInRow.toString() ?? GalleryWidgetData.defaultStyle.imageInRow.toString());
        imagesProp.tryAdd("--visible-rows", (style.pagination.visibleRows || GalleryWidgetData.defaultStyle.pagination.visibleRows).toString());

        this.styleService.setStyle(element, `#${element.id} .gallery-wrapper`, imagesProp.data);

        this.renderImageStyle(element, style);
    }

    private renderImageStyle(element: HTMLElement, style: IGalleryWidgetStyle): void {
        const cssItems = new CssProperties();

        if (style.borderRadius) {
            cssItems.tryAdd("border-top-left-radius", `${style.borderRadius.topLeftPx}px`);
            cssItems.tryAdd("border-top-right-radius", `${style.borderRadius.topRightPx}px`);
            cssItems.tryAdd("border-bottom-left-radius", `${style.borderRadius.bottomLeftPx}px`);
            cssItems.tryAdd("border-bottom-right-radius", `${style.borderRadius.bottomRightPx}px`);
        }

        if (style?.displayShadow) {
            cssItems.tryAdd("box-shadow", `0px 5px 22px ${style.shadowColor}`);
        }

        this.styleService.setStyle(element, `#${element.id} .image-wrapper`, cssItems.data);
    }

    private renderTextStyle(element: HTMLElement, style: IGalleryWidgetStyle): void {
        this.renderFontStyle(element, style);

        const contentWrapperProp = new CssProperties();
        if (style.horizontalAlignment) {
            contentWrapperProp.tryAdd("--text-align", style.horizontalAlignment);
        }

        if (style.overlayColor) {
            contentWrapperProp.tryAdd("--overlay-color", style.overlayColor);
        }

        if (style.verticalAlignment) {
            const alignment = this.verticalAlignmentRenderer.getAlignmentBySelfPosition(style.verticalAlignment);

            contentWrapperProp.tryAdd("justify-content", alignment);
        }

        this.styleService.setStyle(element, `#${element.id} .content-wrapper`, contentWrapperProp.data);
    }

    private renderFontStyle(element: HTMLElement, style: IGalleryWidgetStyle): void {
        const titleFontStyle = new CssProperties();

        titleFontStyle.tryAdd("color", style.fontTitle?.color ?? GalleryWidgetData.defaultStyle.fontTitle?.color);
        titleFontStyle.tryAdd("font-weight", style.fontTitle?.weight ?? GalleryWidgetData.defaultStyle.fontTitle?.weight);
        titleFontStyle.tryAdd("font-family", style.fontTitle?.family ?? GalleryWidgetData.defaultStyle.fontTitle?.family);

        this.styleService.setStyle(element, `#${element.id} .title`, titleFontStyle.data);

        const descriptionFontStyle = new CssProperties();

        descriptionFontStyle.tryAdd("color", style.fontDescription?.color ?? GalleryWidgetData.defaultStyle.fontDescription?.color);
        descriptionFontStyle.tryAdd("font-weight", style.fontDescription?.weight ?? GalleryWidgetData.defaultStyle.fontDescription?.weight);
        descriptionFontStyle.tryAdd("font-family", style.fontDescription?.family ?? GalleryWidgetData.defaultStyle.fontDescription?.family);

        this.styleService.setStyle(element, `#${element.id} .description`, descriptionFontStyle.data);
    }

    private setAttributes(element: HTMLElement, data: IGalleryWidgetData): void {
        const shortData = this.getShortData(data);
        const json = Base64Util.serialize(shortData) || "";

        element.setAttribute(this.DATA_ATTRIBUTE, json);
        if (this.isEditorRendering) {
            element.setAttribute("data-draft", "true");
        }
    }

    private getShortData(data: IGalleryWidgetData): IGalleryWidgetDataShort {
        return {
            isNeedTitle: data.isNeedTitle,
            isNeedDescription: data.isNeedDescription,
            images: data.images,
            imgRatio: data.imgRatio,
            imageBorder: data.style.borderRadius,
            infoPosition: data.infoPosition,
            actionType: data.actionType,
            hoverAnimationType: data.hoverAnimationType,
            style: data.style
        };
    }

    private removeGalleryWrapper(element: HTMLElement): void {
        const galleryWrapper = element.querySelector(".gallery-wrapper");
        galleryWrapper?.remove();
    }

    private cleanupStyle(element: HTMLElement): void {
        const selectors = this.getContentSelectorsInternal(element);
        if (selectors && selectors.length > 0) {
            selectors.forEach(selector => this.styleService.clearStyle(element, selector));
        }
    }

    private renderScript(element: HTMLElement, data: IGalleryWidgetData): void {
        this.scriptRenderer.renderCommon(this.renderedDocument);
        const existingData = this.getData(element) as IGalleryWidgetData;
        if (!GalleryWidgetData.localScriptRerenderNeeded(existingData, data)) {
            this.scriptRenderer.renderLocal(element);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected cleanupInternal(element: HTMLElement): void {
        if (!document.querySelector("[data-widget=galleryWidget]")) {
            this.scriptRenderer.cleanupCommon(this.renderedDocument);
        }
    }

    protected getContentSelectorsInternal(element: HTMLElement): string[] {
        return [
            `#${element.id}`,
            `#${element.id} .gallery-wrapper`,
            `#${element.id} .content-wrapper`,
            `#${element.id} .title`,
            `#${element.id} .description`,
            `#${element.id} .image-wrapper`,
            `#${element.id} .button-wrapper`,
            `#${element.id} .load-more-button`
        ];
    }

}
